import { useEffect, useRef } from "react";
import "@/App.css";
import "@/theme-light.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/context/LangContext";
import { AuthProvider } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { SiteProvider, useSite } from "@/context/SiteContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MiniPlayer } from "@/components/MiniPlayer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Sermons from "@/pages/Sermons";
import Donate from "@/pages/Donate";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordHelp";
import Admin from "@/pages/Admin";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import CustomPage from "@/pages/CustomPage";
import Events, { Department } from "@/pages/Events";
import Pastors, { PastorPage } from "@/pages/Pastors";
import { ThemeApplier, HiddenApplier } from "@/lib/design";
import { EditOverlay } from "@/components/EditOverlay";

const SmoothScroll = () => {
  const lenis = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => {
    const l = new Lenis({ duration: 1.15, smoothWheel: true });
    lenis.current = l;
    let id;
    const raf = (time) => {
      l.raf(time);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      l.destroy();
    };
  }, []);
  useEffect(() => {
    if (lenis.current) lenis.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PublicLayout = () => (
  <>
    <div className="grain" aria-hidden />
    <Header />
    <Outlet />
    <Footer />
  </>
);

const Gate = ({ k, children }) => {
  const { on } = useSite();
  return on(k) ? children : <Navigate to="/" replace />;
};

function App() {
  return (
    <SiteProvider>
    <LangProvider>
      <AuthProvider>
        <PlayerProvider>
          <BrowserRouter>
            <SmoothScroll />
            <ThemeApplier />
            <HiddenApplier />
            <EditOverlay />
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<Gate k="about"><About /></Gate>} />
                <Route path="/sermons" element={<Gate k="sermons"><Sermons /></Gate>} />
                <Route path="/pastors" element={<Gate k="pastors"><Pastors /></Gate>} />
                <Route path="/pastors/:slug" element={<Gate k="pastors"><PastorPage /></Gate>} />
                <Route path="/events" element={<Gate k="gallery"><Events /></Gate>} />
                <Route path="/events/:slug" element={<Gate k="gallery"><Department /></Gate>} />
                <Route path="/donate" element={<Gate k="donate"><Donate /></Gate>} />
                <Route path="/contact" element={<Gate k="contact"><Contact /></Gate>} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/p/:slug" element={<CustomPage />} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/forgot-password" element={<ForgotPassword />} />
              <Route path="/admin/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<Admin />} />
            </Routes>
            <MiniPlayer />
          </BrowserRouter>
          <Toaster position="top-center" theme="light" />
        </PlayerProvider>
      </AuthProvider>
    </LangProvider>
    </SiteProvider>
  );
}

export default App;
